import { useCreateLicenseMutation } from "Api/Mutations/Licenses/useCreateLicenseMutation";
import { useLicenseQuery } from "Api/Queries/Licenses/useLicenseQuery";
import { isCouponError } from "Components/Licenses/CouponInput/couponErrors";
import { LicenseOrderForm } from "Components/Licenses/LicenseOrderForm/LicenseOrderForm";
import { LicenseOrderFormSkeleton } from "Components/Licenses/LicenseOrderForm/LicenseOrderFormSkeleton";
import { LIFETIME_PERIODICITY } from "Components/Licenses/LicenseOrderForm/constants";
import type { LicenseSelectionModel } from "Components/Licenses/LicenseOrderForm/schema";
import { getNextAvailableLicense } from "Components/Licenses/licenseUtils";
import { ResponsiveDialog } from "Components/Shared/Dialogs/ResponsiveDialog";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { useEnsureUserRegion } from "Hooks/useEnsureUserRegion";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import { OwnershipType } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { toast } from "sonner";

const RESOURCES = Resources.Components.LicenseOrder;

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onOrderCreated?: (licensePublicID: string) => void;
};

// The same order flow as the standalone LicenseOrder, but dialog-friendly:
// success closes the dialog instead of switching the page via ?transaction=.
export const OrderLicenseDialog = ({
  isOpen,
  onClose,
  onOrderCreated,
}: Props) => {
  const { t } = useResource();
  // The user's chosen market drives both the offered licenses and the preselected region —
  // never a CZ constant; a region-less user picks in the form and the offer loads on selection.
  const userRegion = useLoggedInUser()?.region ?? undefined;
  const { ensureUserRegion, isSettingRegion } = useEnsureUserRegion();
  // Gated on isOpen — the dialog stays mounted while closed (mobile drawer keeps
  // children mounted), and the license types are only needed once it opens.
  const { data: { items: licenses } = {}, isError } = useLicenseQuery(
    isOpen ? userRegion : undefined,
  );

  const { mutate: createLicense, isPending: isCreateLicensePending } =
    useCreateLicenseMutation({
      onSuccess: (license) => {
        toast.success(t(RESOURCES.CreateSuccess));
        onOrderCreated?.(license.publicID);
        onClose();
      },
      onError: (error) => {
        const couponError = isCouponError(error);
        if (couponError) {
          toast.error(t(RESOURCES.Coupon.Error[couponError]));
        } else {
          toast.error(t(RESOURCES.CreateError));
        }
      },
    });

  const onSubmit = ({
    code,
    region,
    couponCode,
    walletAddress = "",
    isUsingPrivateWallet,
  }: LicenseSelectionModel) => {
    const handleCreateLicense = () =>
      createLicense({
        code,
        region,
        walletAddress,
        couponCode: couponCode ?? null,
        shouldReplaceUnpaidLicenses: false,
        ownershipType: isUsingPrivateWallet
          ? OwnershipType.NonCustodial
          : OwnershipType.Custodial,
        subscriptionPeriodicity: LIFETIME_PERIODICITY,
      });
    const handleRegionError = () =>
      toast.error(t(Resources.Components.RegionRequiredModal.Error));

    ensureUserRegion(region, handleCreateLicense, handleRegionError);
  };

  const defaultLicense = licenses
    ? getNextAvailableLicense(licenses)
    : undefined;

  return (
    <ResponsiveDialog
      title={t(Resources.Hunter.LicensePaymentPending.OrderLicense)}
      isOpen={isOpen}
      onClose={onClose}
    >
      {isError && <ErrorText>{t(RESOURCES.LoadingError)}</ErrorText>}
      {!isError && userRegion && !licenses && <LicenseOrderFormSkeleton />}
      {!isError && (licenses || !userRegion) && (
        <LicenseOrderForm
          onSubmit={onSubmit}
          defaultValues={{ code: defaultLicense?.code, region: userRegion }}
          isSubmitting={isCreateLicensePending || isSettingRegion}
        />
      )}
    </ResponsiveDialog>
  );
};
